import React from "react";
import {
  FaArrowRight,
  FaBookmark,
  FaStar,
  FaMapMarkerAlt,
} from "react-icons/fa";
import "../css/card.css";
import { useNavigate } from "react-router-dom";
import { notifyInfo, notifySuccess } from "../utils/notify";
import { isTripItemSaved, saveTripItem } from "../utils/tripPlanner";

function Card({
  title = "",
  description = "",
  image = "",
  rating = 0,
  price = 0,
  location = "",
  category = "",
  gallery = [],
  amenities = [],
  cat = "",
  lineUp = [],
  shops = [],
  phone = "",
  email = "",
  ticketLink="",
  mapLink,
  routes = [],
  schedule = [],
  operator = "",
  openingHours = "",
  hours = "",
  openStatus = null,
  distanceKm = null,
}) {
  const navigate = useNavigate();

  // Safely parse rating as number and clamp between 0-5
  const safeRating = Math.min(5, Math.max(0, Number(rating) || 0));

  const priceMatches =
    typeof price === "string" ? price.match(/\d+(?:[.,]\d+)?/g) : null;
  const safePrice =
    typeof price === "number"
      ? price
      : priceMatches?.length
      ? Number(priceMatches[0].replace(",", "."))
      : 0;
  const priceLabel =
    typeof price === "string" && priceMatches?.length > 1
      ? `R ${priceMatches.join(" - R ")}`
      : safePrice > 0
      ? `R ${safePrice.toLocaleString("en-ZA", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        })}`
      : "";

  const getDetailState = () => {
    const state = {
      title,
      description,
      image,
      rating: safeRating,
      location,
      category,
      phone,
      email,
      mapLink,
      price: safePrice,
      pricePerNight: safePrice,
      ticketLink,
      routes: Array.isArray(routes) ? routes : [],
      schedule: schedule && typeof schedule === "object" ? schedule : [],
      operator,
      openingHours,
      hours,
    };

    if (cat === "Accommodation") {
      state.gallery = Array.isArray(gallery) ? gallery : [];
      state.amenities = Array.isArray(amenities) ? amenities : [];
    } else if (cat === "Events") {
      state.gallery = Array.isArray(gallery) ? gallery : [];
      state.lineUp = Array.isArray(lineUp) ? lineUp : [];
    } else if (["Restaurants", "Shopping Malls", "Attractions"].includes(cat)) {
      state.gallery = Array.isArray(gallery) ? gallery : [];
      state.amenities = Array.isArray(amenities) ? amenities : [];
      state.shops = Array.isArray(shops) ? shops : [];
    } else if (cat === "Buses") {
      state.gallery = Array.isArray(gallery) ? gallery : [];
    }

    return state;
  };

  const getDetailPath = () => {
    if (cat === "Accommodation") return "/description";
    if (cat === "Events") return "/eventDescription";
    if (cat === "Restaurants") return "/resturantDescription";
    if (cat === "Shopping Malls") return "/mallDescription";
    if (cat === "Attractions") return "/attractionDescription";
    if (cat === "Buses") return "/busDescription";
    return "";
  };

  const plannerItem = {
    title,
    description,
    image,
    category: cat || category || "Saved Idea",
    location,
    detailPath: getDetailPath(),
    detailState: getDetailState(),
  };

  const [saved, setSaved] = React.useState(() => isTripItemSaved(plannerItem));

  const handleCardClick = () => {
    const state = getDetailState();

    if (cat === "Accommodation") {
      navigate(`/description`, { state });
    } else if (cat === "Events") {
      navigate(`/eventDescription`, { state });
    } else if (cat === "Restaurants") {
      navigate(`/resturantDescription`, { state });
    } else if (cat === "Shopping Malls") {
      navigate(`/mallDescription`, { state });
    } else if (cat === "Attractions") {
      navigate(`/attractionDescription`, { state });
    }else if(cat === "Buses"){
      navigate("/busDescription" , {state})
    }
  };

  const handleSaveTrip = (event) => {
    event.stopPropagation();

    const result = saveTripItem(plannerItem);

    if (result.saved) {
      setSaved(true);
      notifySuccess(`${title} saved to your trip planner.`);
      return;
    }

    notifyInfo(`${title} is already in your trip planner.`);
  };

  return (
    <div className="card-category" onClick={handleCardClick}>
      <div className="card-media">
        {image ? (
          <img src={image} alt={title} className="card-image" />
        ) : (
          <div className="card-image card-image-placeholder">
            <span>{category || "Kimberley"}</span>
          </div>
        )}
        {category && <span className="card-category-pill">{category}</span>}
        {openStatus?.known && (
          <span className={`card-status-pill ${openStatus.open ? "open" : "closed"}`}>
            {openStatus.label}
          </span>
        )}
        <button
          type="button"
          className={`card-save-button ${saved ? "saved" : ""}`}
          onClick={handleSaveTrip}
          aria-label={`Save ${title} to trip planner`}
        >
          <FaBookmark />
          <span>{saved ? "Saved" : "Save"}</span>
        </button>
        <div className="card-rating-chip">
          <FaStar />
          <span>{safeRating}/5</span>
        </div>
      </div>

      <div className="card-content">
        <h3 className="card-title">{title}</h3>

        {description && (
          <p className="card-description">
            {description.length > 118
              ? `${description.substring(0, 118)}...`
              : description}
          </p>
        )}

        <div className="card-value-row">
          <div className="card-rating">
            {[...Array(5)].map((_, i) => (
              <FaStar
                key={i}
                className={`star-icon ${i < safeRating ? "filled" : ""}`}
              />
            ))}
          </div>
          {priceLabel && <p className="card-price">{priceLabel}</p>}
        </div>

        <div className="card-info">
          {location && (
            <div className="card-location">
              <FaMapMarkerAlt className="location-icon" />
              {location.length > 60 
                ? `${location.substring(0, 60)}...` 
                : location}
            </div>
          )}
          {typeof distanceKm === "number" && (
            <div className="card-distance">
              Approx. {distanceKm.toFixed(1)} km from Kimberley centre
            </div>
          )}
          <span className="card-details-link">
            View Details
            <FaArrowRight />
          </span>
        </div>
      </div>
    </div>
  );
}

export default Card;
